import { useAuth } from '../../contexts/AuthContext';
import React, { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw,
  Search,
  History,
  ShieldAlert,
  User,
  Building2,
  Briefcase,
  Calendar,
  RotateCcw,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import useEmployeeMasters from '../../hooks/useEmployeeMasters';
import { buildApiUrl } from '../../utils/apiSetup';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const blankEmployee = {
  employee_code: '',
  employee_name: '',
  department: '',
  designation: '',
  designation_type: '',
  current_service_status: '',
  current_basic: 0,
  status: '',
  suspension_date: '',
  revocation_date: '',
  normal_basic_snapshot: 0,
  reduction_percentage: 0,
  payable_basic: 0,
  reason: '',
  remarks: '',
  revocation_remarks: '',
};

const blankHistoryItem = {
  employee_code: '',
  employee_name: '',
  department: '',
  designation: '',
  designation_type: '',
  suspended_on: '',
  revoked_on: '',
  duration: null,
  reduction_percentage: 0,
  normal_basic_snapshot: 0,
  payable_basic: 0,
  reason: '',
  remarks: '',
  status: '',
  revocation_remarks: '',
  current_service_status: '',
  created_at: '',
  updated_at: '',
};

const SuspensionManagement = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('suspension');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [showEmployeeSuggestions, setShowEmployeeSuggestions] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(blankEmployee);
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    employee_id: '',
    department_id: '',
    status: '',
    start_date: '',
    end_date: '',
  });

  const [suspensionForm, setSuspensionForm] = useState({
    suspension_date: todayIso(),
    reduction_percentage: '',
    reason: '',
    remarks: '',
  });

  const [revokeForm, setRevokeForm] = useState({
    revocation_date: todayIso(),
    revocation_remarks: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const { masters } = useEmployeeMasters();
  const departments = masters?.departments || [];

  const currentPreview = useMemo(() => {
    const reduction = Number(suspensionForm.reduction_percentage || 0);
    const base = Number(employeeDetails.current_basic || employeeDetails.normal_basic_snapshot || 0);
    const payable = base * (100 - reduction) / 100;
    return {
      reduction,
      base,
      payable: Number.isFinite(payable) ? payable : 0,
    };
  }, [employeeDetails.current_basic, employeeDetails.normal_basic_snapshot, suspensionForm.reduction_percentage]);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchEmployees(employeeQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeQuery]);

  useEffect(() => {
    if (selectedEmployee) {
      loadEmployeeSuspension(selectedEmployee.id);
    }
  }, [selectedEmployee]);

  useEffect(() => {
    if (!historyFilters.employee_id && !historyFilters.department_id && !historyFilters.status && !historyFilters.start_date && !historyFilters.end_date) {
      return;
    }
    loadHistory();
  }, [historyFilters]);

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    window.clearTimeout(showMessage._timer);
    showMessage._timer = window.setTimeout(() => setMessage(''), 5000);
  };

  const searchEmployees = async (query) => {
    if (!query.trim()) {
      setEmployeeSuggestions([]);
      return;
    }
    try {
      const response = await fetch(buildApiUrl(`/api/v1/benfish-employees/search?query=${encodeURIComponent(query)}&limit=100`), {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setEmployeeSuggestions(data.employees || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const loadEmployeeSuspension = async (employeeId) => {
    setLoadingEmployeeDetails(true);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/suspensions/${employeeId}`), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setEmployeeDetails({
          ...blankEmployee,
          ...data,
        });
        if (data.status === 'ACTIVE') {
          setRevokeForm((prev) => ({
            ...prev,
            revocation_date: todayIso(),
          }));
        } else {
          setSuspensionForm({
            suspension_date: todayIso(),
            reduction_percentage: '',
            reason: '',
            remarks: '',
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingEmployeeDetails(false);
    }
  };

  // Tab fallback logic
  useEffect(() => {
    const tabs = [
      { id: 'suspension', perms: ['suspension:suspend', 'suspension:revoke'] },
      { id: 'history', perms: ['suspension:view_history'] }
    ];
    const firstAllowed = tabs.find(t => t.perms.some(p => hasPermission(p)));
    const currentAllowed = tabs.find(t => t.id === activeTab)?.perms.some(p => hasPermission(p));
    
    if (firstAllowed && !currentAllowed) {
        setActiveTab(firstAllowed.id);
    }
  }, [hasPermission, activeTab]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(historyFilters).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        }
      });
      const response = await fetch(buildApiUrl(`/v1/suspensions/history${params.toString() ? `?${params.toString()}` : ''}`), {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setHistory(await response.json());
      }
    } catch (error) {
      console.error(error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeQuery(`${employee.emp_code} - ${employee.full_name}`);
    setShowEmployeeSuggestions(false);
    setSelectedHistoryItem(null);
  };

  const handleSuspend = async (event) => {
    event.preventDefault();
    if (!selectedEmployee) {
      showMessage('Please select an employee first.', 'error');
      return;
    }
    if (suspensionForm.reduction_percentage === '') {
      showMessage('Reduction percentage is required.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/suspensions'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          employee_id: selectedEmployee.id,
          suspension_date: suspensionForm.suspension_date,
          reduction_percentage: Number(suspensionForm.reduction_percentage),
          reason: suspensionForm.reason,
          remarks: suspensionForm.remarks,
        }),
      });
      if (response.ok) {
        showMessage('Employee suspended successfully.', 'success');
        await loadEmployeeSuspension(selectedEmployee.id);
        await loadHistory();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Unable to suspend employee.', 'error');
      }
    } catch (error) {
      showMessage('Unable to suspend employee.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (event) => {
    event.preventDefault();
    if (!selectedEmployee) {
      showMessage('Please select an employee first.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/suspensions/revoke'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          employee_id: selectedEmployee.id,
          revocation_date: revokeForm.revocation_date,
          revocation_remarks: revokeForm.revocation_remarks,
        }),
      });
      if (response.ok) {
        showMessage('Suspension revoked successfully.', 'success');
        await loadEmployeeSuspension(selectedEmployee.id);
        await loadHistory();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Unable to revoke suspension.', 'error');
      }
    } catch (error) {
      showMessage('Unable to revoke suspension.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const renderSuspensionTab = () => {
    const isSuspended = employeeDetails.status === 'ACTIVE';

    return (
      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Employee Search</h3>
                <p className="text-sm text-gray-500">Search by employee code or name to manage suspension status.</p>
              </div>
              <button type="button" onClick={loadHistory} className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600">
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh History
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
              <input
                value={employeeQuery}
                onChange={(event) => {
                  setEmployeeQuery(event.target.value);
                  setShowEmployeeSuggestions(true);
                }}
                onFocus={() => setShowEmployeeSuggestions(true)}
                onBlur={() => setTimeout(() => setShowEmployeeSuggestions(false), 150)}
                placeholder="Search employee..."
                className="w-full rounded-lg border border-gray-200 px-10 py-3 text-sm"
              />
              {showEmployeeSuggestions && employeeSuggestions.length > 0 ? (
                <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                  {employeeSuggestions.map((employee) => (
                    <button
                      key={employee.id}
                      type="button"
                      className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleEmployeeSelect(employee)}
                    >
                      <span>{employee.emp_code} - {employee.full_name}</span>
                      <span className="text-xs text-gray-400">{employee.department_name || '-'}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {selectedEmployee && (
            <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Employee Snapshot</h3>
                  <p className="text-sm text-gray-500">Current service status, salary snapshot, and suspension details.</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${employeeDetails.current_service_status === 'SUSPENDED' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {employeeDetails.current_service_status || 'ACTIVE'}
                </span>
              </div>
              {loadingEmployeeDetails ? (
                <p className="text-sm text-gray-500">Loading employee details...</p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  <InfoCard label="Employee Code" value={employeeDetails.employee_code} icon={User} />
                  <InfoCard label="Employee Name" value={employeeDetails.employee_name} icon={User} />
                  <InfoCard label="Department" value={employeeDetails.department} icon={Building2} />
                  <InfoCard label="Designation" value={employeeDetails.designation} icon={Briefcase} />
                  <InfoCard label="Current Basic" value={`₹${Number(employeeDetails.current_basic || 0).toLocaleString('en-IN')}`} icon={Calendar} />
                  <InfoCard label="Suspension Pay" value={`₹${Number(employeeDetails.payable_basic || 0).toLocaleString('en-IN')}`} icon={ShieldAlert} />
                  <InfoCard label="Reduction %" value={`${Number(employeeDetails.reduction_percentage || 0)}%`} icon={AlertTriangle} />
                  <InfoCard label="Suspended On" value={employeeDetails.suspension_date || '-'} icon={Calendar} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          {selectedEmployee && !isSuspended && (
            <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">Suspend Employee</h3>
              </div>
              <form onSubmit={handleSuspend} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="text-sm text-gray-700">
                    <span className="mb-1 block font-medium">Suspension Date</span>
                    <input
                      type="date"
                      value={suspensionForm.suspension_date}
                      onChange={(e) => setSuspensionForm((prev) => ({ ...prev, suspension_date: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2"
                      required
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    <span className="mb-1 block font-medium">Reduction %</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={suspensionForm.reduction_percentage}
                      onChange={(e) => setSuspensionForm((prev) => ({ ...prev, reduction_percentage: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2"
                      required
                    />
                  </label>
                </div>
                <label className="block text-sm text-gray-700">
                  <span className="mb-1 block font-medium">Reason</span>
                  <textarea
                    value={suspensionForm.reason}
                    onChange={(e) => setSuspensionForm((prev) => ({ ...prev, reason: e.target.value }))}
                    rows="3"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2"
                    required
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  <span className="mb-1 block font-medium">Remarks</span>
                  <textarea
                    value={suspensionForm.remarks}
                    onChange={(e) => setSuspensionForm((prev) => ({ ...prev, remarks: e.target.value }))}
                    rows="2"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2"
                  />
                </label>
                <div className="rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
                  <div className="font-medium text-amber-900">Live preview</div>
                  <div>Current Basic: ₹{Number(currentPreview.base || 0).toLocaleString('en-IN')}</div>
                  <div>Reduction: {Number(currentPreview.reduction || 0)}%</div>
                  <div>Calculated Suspension Pay: <strong>₹{Number(currentPreview.payable || 0).toLocaleString('en-IN')}</strong></div>
                </div>
                {hasPermission("suspension:suspend") && (
<button
                  type="submit"
                  disabled={submitting || !suspensionForm.reason.trim() || suspensionForm.reduction_percentage === ''}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {submitting ? 'Submitting...' : 'Suspend Employee'}
                </button>
)}
              </form>
            </div>
          )}

          {selectedEmployee && isSuspended && (
            <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Suspension Details</h3>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <InfoCard label="Suspension Date" value={employeeDetails.suspension_date || '-'} icon={Calendar} />
                <InfoCard label="Revocation Date" value={employeeDetails.revocation_date || '-'} icon={Calendar} />
                <InfoCard label="Normal Basic" value={`₹${Number(employeeDetails.normal_basic_snapshot || employeeDetails.current_basic || 0).toLocaleString('en-IN')}`} icon={ShieldAlert} />
                <InfoCard label="Payable Basic" value={`₹${Number(employeeDetails.payable_basic || 0).toLocaleString('en-IN')}`} icon={ShieldAlert} />
                <InfoCard label="Reduction %" value={`${Number(employeeDetails.reduction_percentage || 0)}%`} icon={AlertTriangle} />
                <InfoCard label="Current Status" value={employeeDetails.current_service_status || 'SUSPENDED'} icon={AlertTriangle} />
              </div>
              <div className="mt-4 grid gap-4">
                <InfoCard label="Reason" value={employeeDetails.reason} icon={FileText} />
                <InfoCard label="Remarks" value={employeeDetails.remarks} icon={FileText} />
                <InfoCard label="Revocation Remarks" value={employeeDetails.revocation_remarks} icon={FileText} />
              </div>

              <form onSubmit={handleRevoke} className="mt-6 space-y-4">
                <label className="block text-sm text-gray-700">
                  <span className="mb-1 block font-medium">Revocation Date</span>
                  <input
                    type="date"
                    value={revokeForm.revocation_date}
                    onChange={(e) => setRevokeForm((prev) => ({ ...prev, revocation_date: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2"
                    required
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  <span className="mb-1 block font-medium">Revocation Remarks</span>
                  <textarea
                    value={revokeForm.revocation_remarks}
                    onChange={(e) => setRevokeForm((prev) => ({ ...prev, revocation_remarks: e.target.value }))}
                    rows="3"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2"
                  />
                </label>
                {hasPermission("suspension:revoke") && (
<button
                  type="submit"
                  disabled={submitting}
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {submitting ? 'Submitting...' : 'Revoke Suspension'}
                </button>
)}
              </form>
            </div>
          )}

          {selectedEmployee && !isSuspended && (
            <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Employee is active and eligible for a new suspension.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHistoryTab = () => {
    const selected = selectedHistoryItem || blankHistoryItem;

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Suspension History</h3>
              <p className="text-sm text-gray-500">Every suspension episode is retained here, including revoked records.</p>
            </div>
            <button type="button" onClick={loadHistory} className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600">
              <RefreshCw className="mr-2 h-4 w-4" />
              Reload
            </button>
          </div>

          <div className="mb-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <select
              value={historyFilters.employee_id}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, employee_id: e.target.value }))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">All Employees</option>
              {employeeSuggestions.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.emp_code} - {employee.full_name}
                </option>
              ))}
            </select>
            <select
              value={historyFilters.department_id}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, department_id: e.target.value }))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">All Departments</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.label}
                </option>
              ))}
            </select>
            <select
              value={historyFilters.status}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="REVOKED">Revoked</option>
            </select>
            <input
              type="date"
              value={historyFilters.start_date}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, start_date: e.target.value }))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={historyFilters.end_date}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, end_date: e.target.value }))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>

          {historyLoading ? (
            <p className="text-sm text-gray-500">Loading history...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500">No suspension records found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Employee</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Department</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Designation</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Suspension Date</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Revocation Date</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Duration</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Reduction %</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Normal Basic</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Suspension Pay</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {history.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{item.employee_name}</p>
                        <p className="text-xs text-gray-500">{item.employee_code}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{item.department || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{item.designation || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{item.suspended_on}</td>
                      <td className="px-4 py-3 text-gray-700">{item.revoked_on || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{item.duration ?? '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{Number(item.reduction_percentage || 0)}%</td>
                      <td className="px-4 py-3 text-gray-700">₹{Number(item.normal_basic_snapshot || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-gray-700">₹{Number(item.payable_basic || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${item.status === 'ACTIVE' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setSelectedHistoryItem(item)}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedHistoryItem && (
          <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">History Details</h3>
                <p className="text-sm text-gray-500">Complete details for the selected suspension episode.</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedHistoryItem(null)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600"
              >
                Close
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <InfoCard label="Employee" value={`${selected.employee_name} (${selected.employee_code})`} icon={User} />
              <InfoCard label="Department" value={selected.department} icon={Building2} />
              <InfoCard label="Designation" value={selected.designation} icon={Briefcase} />
              <InfoCard label="Suspension Date" value={selected.suspended_on} icon={Calendar} />
              <InfoCard label="Revocation Date" value={selected.revoked_on || '-'} icon={Calendar} />
              <InfoCard label="Duration" value={selected.duration ?? '-'} icon={History} />
              <InfoCard label="Reduction %" value={`${Number(selected.reduction_percentage || 0)}%`} icon={AlertTriangle} />
              <InfoCard label="Normal Basic" value={`₹${Number(selected.normal_basic_snapshot || 0).toLocaleString('en-IN')}`} icon={ShieldAlert} />
              <InfoCard label="Suspension Pay" value={`₹${Number(selected.payable_basic || 0).toLocaleString('en-IN')}`} icon={ShieldAlert} />
              <InfoCard label="Current Status" value={selected.current_service_status || '-'} icon={AlertTriangle} />
            </div>
            <div className="mt-4 grid gap-4">
              <InfoCard label="Reason" value={selected.reason} icon={FileText} />
              <InfoCard label="Remarks" value={selected.remarks} icon={FileText} />
              <InfoCard label="Revocation Remarks" value={selected.revocation_remarks} icon={FileText} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Suspension Management</h2>
            <p className="text-sm text-gray-500">Manage employee suspension and review history from one clean workflow.</p>
          </div>
          <button type="button" onClick={loadHistory} className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>

        {message && (
          <div className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
            messageType === 'success' ? 'border-green-200 bg-green-50 text-green-700'
              : messageType === 'error' ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-blue-200 bg-blue-50 text-blue-700'
          }`}>
            {message}
          </div>
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {[
              { id: 'suspension', label: 'Suspension', icon: ShieldAlert, requiredPermission: ['suspension:suspend', 'suspension:revoke'] },
              { id: 'history', label: 'History', icon: History, requiredPermission: ['suspension:view_history'] },
            ].filter(tab => tab.requiredPermission.some(p => hasPermission(p))).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'suspension' && renderSuspensionTab()}
      {activeTab === 'history' && renderHistoryTab()}
    </div>
  );
};

const InfoCard = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
    <div className="mb-2 flex items-center gap-2 text-gray-500">
      <Icon className="h-4 w-4" />
      <span className="text-xs uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-sm font-medium text-gray-900">{value || '-'}</p>
  </div>
);

export default SuspensionManagement;
